// ./components/Hero.js
import React, { useEffect, useMemo, useState } from "react";

/* -------- Parallax -------- */
function useParallax(speed = 0.25) {
  const [y, setY] = useState(0);
  useEffect(() => {
    const onScroll = () => setY(window.scrollY * speed);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [speed]);
  return y;
}

/* -------- Arabesque SVG Pattern -------- */
const ARABESQUE_SVG =
  "<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'>\
    <defs>\
      <g id='m' opacity='0.95'>\
        <path d='M0,60 C30,60 30,0 60,0' fill='none' stroke='#C49A6C' stroke-width='0.8'/>\
        <path d='M60,0 C90,0 90,60 120,60' fill='none' stroke='#C49A6C' stroke-width='0.8'/>\
        <path d='M0,60 C30,60 30,120 60,120' fill='none' stroke='#C49A6C' stroke-width='0.8'/>\
        <path d='M60,120 C90,120 90,60 120,60' fill='none' stroke='#C49A6C' stroke-width='0.8'/>\
      </g>\
    </defs>\
    <use href='#m'/>\
  </svg>";

/**
 * Hero
 */
export default function Hero({
  t,
  dir = "ltr",
  routes = {},
  UI = { garnet: "#8B1E3F", teal: "#008b8b" },
  images,
  logoSrc = "/images/logo.svg",
  showArabesque = true,
  arabesqueSize = "120px 120px",
  arabesqueOpacity = 0.18, // start high so it’s visible
  subtitleBg = true,
}) {
  const heroImages = useMemo(
    () =>
      images && images.length
        ? images
        : ["/images/hero1.jpg", "/images/hero2.jpg", "/images/hero3.jpg"],
    [images]
  );

  const [current, setCurrent] = useState(0);

  // Rotate slides
  useEffect(() => {
    const id = setInterval(
      () => setCurrent((p) => (p + 1) % heroImages.length),
      5000
    );
    return () => clearInterval(id);
  }, [heroImages.length]);

  // Preload
  useEffect(() => {
    heroImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [heroImages]);

  const shift = useParallax(0.25);

  return (
    <section
      aria-label="Hero"
      style={{
        position: "relative",
        color: "#fff",
        minHeight: "60vh",
        overflow: "hidden",
      }}
    >
      {/* Background images */}
      {heroImages.map((img, i) => (
        <div
          key={i}
          aria-hidden={i !== current}
          style={{
            backgroundImage: `url('${img}')`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: `center ${-shift}px`,
            position: "absolute",
            inset: 0,
            opacity: i === current ? 1 : 0,
            transition:
              "opacity 1.5s ease-in-out, background-position 0.2s linear",
            willChange: "opacity, background-position",
          }}
        />
      ))}

      {/* Arabesque overlay */}
      {showArabesque && (
  <div
    aria-hidden
    style={{
      position: "absolute",
      inset: 0,
      zIndex: 2,                 // slides (0), pattern (2), content (3)
      pointerEvents: "none",
      opacity: 0.14,             // overall strength
      backgroundImage: "url('/images/star.svg')", // <-- your file
      backgroundRepeat: "no-repeat",
      backgroundSize: "1000px 1000px", // smaller = denser
      backgroundPosition: "left top", // try "left top" if needed
    }}
  />
)}


      {/* Content */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            flexDirection: "column",
            borderRadius: 12,
            padding: "12px 16px",
            maxWidth: "min(90vw, 980px)",
            textAlign: "center",
            transform: `translateY(${Math.min(shift * 0.25, 20)}px)`,
            transition: "transform 0.2s linear",
            zIndex: 2, // above arabesque
          }}
        >
          {/* Logo */}
          <img
            src={logoSrc}
            alt={dir === "rtl" ? "شعار غرناطة" : "Granada Logo"}
            style={{ height: 110, width: "auto" }}
          />

          {/* Small top text */}
          {t?.heroTop && (
            <div
              style={{
                letterSpacing: 3,
                textTransform: "uppercase",
                fontSize: "clamp(10px, 2vw, 12px)",
                fontWeight: "bold",
                color: "#b88a44",
                background: "rgba(255,255,255,0.7)",
                padding: "4px 8px",
                borderRadius: 6,
              }}
            >
              {t.heroTop}
            </div>
          )}

          {/* Title */}
          <h1
            style={{
              fontSize: "clamp(26px, 5vw, 46px)",
              margin: "8px 0 6px",
              lineHeight: 1.1,
              color: UI?.garnet || "#8B1E3F",
            }}
          >
            {t?.heroTitle}
          </h1>

          {/* Subtitle */}
          {t?.heroLead && (
            <p
              style={{
                fontSize: "clamp(14px, 2.4vw, 18px)",
                margin: "4px 0 10px",
                lineHeight: 1.45,
                color: subtitleBg ? "#111" : "#fff",
                background: subtitleBg
                  ? "rgba(255,255,255,0.6)"
                  : "transparent",
                padding: subtitleBg ? "6px 10px" : 0,
                borderRadius: subtitleBg ? 8 : 0,
                display: "inline-block",
              }}
            >
              {t.heroLead}
            </p>
          )}

          {/* CTAs */}
          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "center",
              marginTop: 14,
              flexWrap: "wrap",
            }}
          >
            <a
              href={routes.floors || "#floors"}
              style={{
                background: UI?.garnet || "#8B1E3F",
                color: "#fff",
                padding: "10px 14px",
                borderRadius: 10,
                textDecoration: "none",
                boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
              }}
            >
              {t?.ctaPrimary}
            </a>

            <a
              href={routes.contact || "#contact"}
              style={{
                background: UI?.teal || "#008b8b",
                border: `1px solid ${UI?.teal || "#008b8b"}`,
                color: "#fff",
                padding: "10px 14px",
                borderRadius: 10,
                textDecoration: "none",
              }}
            >
              {t?.ctaSecondary}
            </a>
          </div>
        </div>
      </div>

      {/* Designed by */}
      {t?.designedBy && (
        <div
          style={{
            position: "absolute",
            bottom: 10,
            ...(dir === "rtl" ? { left: 20 } : { right: 20 }),
            color: "#fff",
            fontSize: 13,
            opacity: 0.9,
            fontWeight: 500,
            textShadow: "0 1px 3px rgba(0,0,0,0.6)",
            zIndex: 2,
            userSelect: "none",
            pointerEvents: "none",
            letterSpacing: 0.3,
          }}
        >
          {t.designedBy}
        </div>
      )}
    </section>
  );
}
